<template>
    <v-card :loading="loading">
        <v-card-title class="text-h5">
            <v-btn
                class="mr-3"
                depressed
                color="primary"
                icon
                rounded
                @click="closeDialog"
            >
                <v-icon>mdi-close</v-icon>
            </v-btn>
            {{ createForm ? 'Add custom location' : 'Edit custom location' }}
        </v-card-title>
        <v-card-text>
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col cols="12" md="6">
                        <v-combobox
                            v-model="form.country"
                            :items="countries"
                            prepend-icon="mdi-earth"
                            label="Country *"
                            required
                            :rules="[rules.required]"
                            @change="selectState"
                        />
                    </v-col>
                    <v-col cols="12" md="6" v-if="requiresShortName">
                        <v-text-field
                            v-model="form.shortName"
                            prepend-icon="mdi-alphabetical"
                            label="Country code *"
                            placeholder="e.g. US"
                            hint="e.g. US, CO, MX"
                            persistent-hint
                            :rules="[rules.required, rules.countryCode]"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-combobox
                            v-if="!isPackageCountry"
                            v-model="form.state"
                            :items="states"
                            prepend-icon="mdi-map-marker-radius"
                            label="State *"
                            :rules="[rules.required]"
                            :disabled="!form.country"
                        />
                        <v-autocomplete
                            v-else
                            v-model="form.state"
                            :items="states"
                            prepend-icon="mdi-map-marker-radius"
                            label="State *"
                            required
                            :rules="[rules.required]"
                            :disabled="!form.country"
                            @change="selectCity"
                        />
                    </v-col>
                    <v-col cols="12" md="6">
                        <v-combobox
                            v-if="!isPackageCountry"
                            v-model="form.city"
                            prepend-icon="mdi-city"
                            label="City *"
                            :rules="[rules.required]"
                            :disabled="!form.state"
                        />
                        <v-combobox
                            v-else
                            v-model="form.city"
                            :items="cities"
                            prepend-icon="mdi-city"
                            label="City *"
                            :rules="[rules.required]"
                            :disabled="!form.state"
                            item-text="text"
                            item-value="text"
                            :return-object="false"
                        />
                    </v-col>
                </v-row>
            </v-form>
        </v-card-text>
        <v-card-actions>
            <v-spacer />
            <v-btn
                rounded
                color="primary"
                :disabled="!valid || !editSettingPermission"
                :loading="loading"
                @click="saveLocation"
            >
                SAVE
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
const countrycitystate = require('countrycitystatejson')

export default {
    name: 'CustomLocationForm',
    props: {
        setting: {
            type: Object,
            required: true,
        },
        createForm: {
            type: Boolean,
            default: true,
        },
        originalLocation: {
            type: Object,
            default: () => ({}),
        },
        editSettingPermission: {
            type: Boolean,
            default: false,
        },
        existingLocations: {
            type: Array,
            default: () => [],
        },
    },
    data: () => ({
        loading: false,
        valid: false,
        listCountries: [],
        countries: [],
        states: [],
        cities: [],
        shortNameCity: '',
        form: {
            country: null,
            state: null,
            city: null,
            shortName: null,
        },
        rules: {
            required: value => !!value || 'The value is required',
            countryCode: value =>
                !value ||
                /^[A-Za-z]{2,3}$/.test(String(value).trim()) ||
                'Use 2–3 letters (e.g. US, CO, MX)',
        },
        duplicateMessage:
            'This location already exists (same country, state, and city).',
        countryCodeTakenMessage:
            'Country code already exists for a custom country.',
        countryCodeInPackageMessage:
            'Country code already exists in the catalog (e.g. US, CO, MX).',
    }),
    computed: {
        isPackageCountry() {
            if (!this.form.country) {
                return false
            }
            const key = this.normalizeKey(this.form.country)
            return countrycitystate
                .getCountries()
                .some(entry => entry.name.trim().toLowerCase() === key)
        },
        isKnownCustomCountry() {
            if (!this.form.country || this.isPackageCountry) {
                return false
            }
            const key = this.normalizeKey(this.form.country)
            return (this.existingLocations || []).some(
                location =>
                    this.normalizeKey(location.country) === key &&
                    location.shortName
            )
        },
        requiresShortName() {
            if (!this.form.country) {
                return false
            }
            return !this.isPackageCountry && !this.isKnownCustomCountry
        },
    },
    mounted() {
        this.buildCountryList()
        if (!this.createForm) {
            this.form = {
                country: this.originalLocation.country,
                state: this.originalLocation.state,
                city: this.originalLocation.city,
                shortName: this.originalLocation.shortName || null,
            }
            this.selectState(this.form.country, true)
            if (this.isPackageCountry) {
                this.selectCity(this.form.state, true)
            }
        }
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        normalizeKey(value) {
            return String(value || '')
                .trim()
                .toLowerCase()
        },
        closeDialog() {
            this.$emit('close')
        },
        findCountryEntry(countryName) {
            const key = this.normalizeKey(countryName)
            return this.listCountries.find(
                entry => this.normalizeKey(entry.name) === key
            )
        },
        resolveCountryShortName(countryName) {
            const entry = this.findCountryEntry(countryName)
            if (entry && entry.shortName) {
                return String(entry.shortName)
                    .trim()
                    .toUpperCase()
            }
            const key = this.normalizeKey(countryName)
            const custom = (this.existingLocations || []).find(
                location =>
                    this.normalizeKey(location.country) === key &&
                    location.shortName
            )
            return custom
                ? String(custom.shortName)
                      .trim()
                      .toUpperCase()
                : String(this.form.shortName || '')
                      .trim()
                      .toUpperCase()
        },
        buildCountryList() {
            const byName = new Map()
            countrycitystate.getCountries().forEach(entry => {
                byName.set(entry.name.trim().toLowerCase(), {
                    name: entry.name,
                    shortName: entry.shortName,
                    fromPackage: true,
                })
            })
            ;(this.existingLocations || []).forEach(location => {
                const key = this.normalizeKey(location.country)
                if (!key || byName.has(key) || !location.shortName) {
                    return
                }
                byName.set(key, {
                    name: String(location.country).trim(),
                    shortName: String(location.shortName).trim(),
                    fromPackage: false,
                })
            })
            this.listCountries = [...byName.values()].sort((a, b) =>
                a.name.localeCompare(b.name)
            )
            this.countries = this.listCountries.map(entry => entry.name)
        },
        customStatesForCountry(countryName) {
            const key = this.normalizeKey(countryName)
            return [
                ...new Set(
                    (this.existingLocations || [])
                        .filter(
                            location =>
                                this.normalizeKey(location.country) === key
                        )
                        .map(location => String(location.state || '').trim())
                        .filter(Boolean)
                ),
            ].sort((a, b) => a.localeCompare(b))
        },
        selectState(event, keepValues = false) {
            if (!keepValues) {
                this.form.state = null
                this.form.city = null
            }
            this.states = []
            this.cities = []
            if (!event) {
                return
            }
            const entry = this.findCountryEntry(event)
            const packageCountry = countrycitystate
                .getCountries()
                .some(
                    item =>
                        item.name.trim().toLowerCase() ===
                        this.normalizeKey(event)
                )
            if (packageCountry && entry && entry.shortName) {
                this.shortNameCity = entry.shortName
                this.states = (
                    countrycitystate.getStatesByShort(this.shortNameCity) || []
                ).sort((a, b) => a.localeCompare(b))
            } else if (entry && entry.shortName) {
                this.shortNameCity = entry.shortName
                this.form.shortName = entry.shortName
                this.states = this.customStatesForCountry(event)
            } else if (this.form.shortName) {
                this.shortNameCity = this.form.shortName
            }
        },
        selectCity(event, keepValues = false) {
            if (!keepValues) {
                this.form.city = null
            }
            this.cities = []
            if (!event || !this.shortNameCity) {
                return
            }
            const data =
                countrycitystate.getCities(this.shortNameCity, event) || []
            this.cities = data
                .map(city => ({ text: city }))
                .sort((a, b) => a.text.localeCompare(b.text))
        },
        normalizeCity(value) {
            if (value && value.text) {
                return value.text
            }
            return value
        },
        locationTripleKey(country, state, city) {
            return `${this.normalizeKey(country)}|${this.normalizeKey(
                state
            )}|${this.normalizeKey(city)}`
        },
        packageHasLocationTriple(country, state, city) {
            const targetCountry = this.normalizeKey(country)
            const targetState = this.normalizeKey(state)
            const targetCity = this.normalizeKey(city)
            const countryMatch = countrycitystate
                .getCountries()
                .find(
                    entry => entry.name.trim().toLowerCase() === targetCountry
                )
            if (!countryMatch) {
                return false
            }
            const packageStates =
                countrycitystate.getStatesByShort(countryMatch.shortName) || []
            const stateMatch = packageStates.find(
                entry => String(entry).trim().toLowerCase() === targetState
            )
            if (!stateMatch) {
                return false
            }
            const packageCities =
                countrycitystate.getCities(
                    countryMatch.shortName,
                    stateMatch
                ) || []
            return packageCities.some(
                entry => String(entry).trim().toLowerCase() === targetCity
            )
        },
        packageShortNameExists(shortName) {
            const normalized = this.normalizeKey(shortName)
            return countrycitystate
                .getCountries()
                .some(
                    entry =>
                        String(entry.shortName || '')
                            .trim()
                            .toLowerCase() === normalized
                )
        },
        customShortNameUsedByOtherCountry(shortName, country) {
            const normalizedCode = this.normalizeKey(shortName)
            const normalizedCountry = this.normalizeKey(country)
            const excludeId = this.createForm
                ? null
                : this.originalLocation.id
            return (this.existingLocations || []).some(location => {
                if (excludeId && location.id === excludeId) {
                    return false
                }
                if (
                    String(location.shortName || '')
                        .trim()
                        .toLowerCase() !== normalizedCode
                ) {
                    return false
                }
                return this.normalizeKey(location.country) !== normalizedCountry
            })
        },
        isDuplicateLocation(country, state, city) {
            if (this.packageHasLocationTriple(country, state, city)) {
                return true
            }
            const targetKey = this.locationTripleKey(country, state, city)
            const excludeId = this.createForm
                ? null
                : this.originalLocation.id
            return (this.existingLocations || []).some(location => {
                if (excludeId && location.id === excludeId) {
                    return false
                }
                return (
                    this.locationTripleKey(
                        location.country,
                        location.state,
                        location.city
                    ) === targetKey
                )
            })
        },
        async saveLocation() {
            try {
                this.loading = true
                const needsCountryCode = !this.isPackageCountry
                const shortName = needsCountryCode
                    ? this.resolveCountryShortName(this.form.country)
                    : undefined
                if (needsCountryCode) {
                    if (!/^[A-Za-z]{2,3}$/.test(String(shortName || ''))) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: 'Use 2–3 letters (e.g. US, CO, MX)',
                        })
                        return
                    }
                    if (this.packageShortNameExists(shortName)) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: this.countryCodeInPackageMessage,
                        })
                        return
                    }
                    if (
                        this.customShortNameUsedByOtherCountry(
                            shortName,
                            this.form.country
                        )
                    ) {
                        this.setErrorItems({
                            source: this.$options.name,
                            message: this.countryCodeTakenMessage,
                        })
                        return
                    }
                }
                const payload = {
                    settingId: this.setting.id,
                    country: this.form.country,
                    state: this.form.state,
                    city: this.normalizeCity(this.form.city),
                    shortName,
                }
                if (
                    this.isDuplicateLocation(
                        payload.country,
                        payload.state,
                        payload.city
                    )
                ) {
                    this.setErrorItems({
                        source: this.$options.name,
                        message: this.duplicateMessage,
                    })
                    return
                }
                if (this.createForm) {
                    const customLocation = await API.addCustomLocation(payload)
                    this.$emit('saved', customLocation)
                } else {
                    const customLocation = await API.updateCustomLocation({
                        id: this.originalLocation.id,
                        ...payload,
                    })
                    this.$emit('updated', customLocation)
                }
                this.closeDialog()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
    },
}
</script>
